import { useState } from 'react';
import { createPortal } from 'react-dom';
import { C, ff } from '../../Dashboard/components/dashboardConstants';

function isImageUrl(url, type) {
  if (type && type.startsWith('IMAGE')) return true;
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
}

function FileIcon({ type }) {
  if (type && type.startsWith('IMAGE')) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="3" stroke={C.sub} strokeWidth="1.5"/>
        <circle cx="8.5" cy="8.5" r="1.5" fill={C.sub}/>
        <path d="M3 15l5-5 4 4 3-3 6 6" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9L13 2z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13 2v7h7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <polygon points="5,3 19,12 5,21" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function SubLabel({ children }) {
  return (
    <span style={{
      fontFamily: ff, fontSize: '13px', fontWeight: 700,
      color: C.bk, letterSpacing: '-0.03em',
    }}>
      {children}
    </span>
  );
}

function EmptyText({ text }) {
  return (
    <span style={{ fontFamily: ff, fontSize: '13px', color: C.g400, letterSpacing: '-0.03em' }}>
      {text}
    </span>
  );
}

function ImageLightbox({ url, onClose }) {
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '720px', maxWidth: '90vw',
          height: '520px', maxHeight: '85vh',
          background: '#111', borderRadius: '16px',
          overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <img
          src={url} alt="첨부 이미지"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>
    </div>,
    document.body
  );
}

export default function DetailSubmission({ data }) {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const hasFiles = data.files && data.files.length > 0;
  const hasVideos = data.videos && data.videos.length > 0;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* 하나의 카드 안에 모든 섹션 */}
        <div style={{
          background: C.white,
          border: `1px solid ${C.g200}`,
          borderRadius: '16px',
          padding: '22px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}>
          <h2 style={{ fontFamily: ff, fontSize: '16px', fontWeight: 700, color: C.bk, margin: 0, paddingBottom: '4px' }}>제출한 정보</h2>

          {/* 자기소개 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <SubLabel>자기소개</SubLabel>
            <span style={{
              fontFamily: ff, fontSize: '18px', fontWeight: 400,
              color: C.bk, lineHeight: '170%', letterSpacing: '-0.03em',
              whiteSpace: 'pre-wrap',
            }}>
              {data.intro || '—'}
            </span>
          </div>

          {/* 파일 첨부 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '0' }}>
            <SubLabel>파일 첨부</SubLabel>
            {!hasFiles
              ? <EmptyText text="첨부된 파일이 없습니다." />
              : data.files.map((f, i) => {
                const isImg = isImageUrl(f.url, f.type);
                const isLast = i === data.files.length - 1;
                return (
                  <div key={f.id ?? i}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
                      <div
                        onClick={isImg && f.url ? () => setLightboxUrl(f.url) : undefined}
                        style={{
                          width: '34px', height: '34px', borderRadius: '8px',
                          border: `1px solid ${C.g200}`, background: '#F7F7F9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, overflow: 'hidden',
                          cursor: isImg && f.url ? 'pointer' : 'default',
                        }}
                      >
                        {isImg && f.url ? (
                          <img
                            src={f.url} alt={f.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <FileIcon type={f.type} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {f.url && !isImg ? (
                          <a
                            href={f.url} target="_blank" rel="noreferrer"
                            style={{
                              fontFamily: ff, fontSize: '13px', fontWeight: 500, color: C.bk,
                              letterSpacing: '-0.03em', textDecoration: 'none',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            {f.name}
                          </a>
                        ) : (
                          <span
                            onClick={isImg && f.url ? () => setLightboxUrl(f.url) : undefined}
                            style={{
                              fontFamily: ff, fontSize: '13px', fontWeight: 500, color: C.bk,
                              letterSpacing: '-0.03em',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              cursor: isImg && f.url ? 'pointer' : 'default',
                            }}
                          >
                            {f.name}
                          </span>
                        )}
                        {(f.type || f.size) && (
                          <span style={{ fontFamily: ff, fontSize: '12px', color: C.sub, letterSpacing: '-0.03em' }}>
                            {[f.type, f.size].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </div>
                    </div>
                    {!isLast && <div style={{ height: '1px', background: C.g200 }} />}
                  </div>
                );
              })
            }
          </div>

          {/* 영상 링크 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '0' }}>
            <SubLabel>영상 링크</SubLabel>
            {!hasVideos
              ? <EmptyText text="첨부된 영상 링크가 없습니다." />
              : data.videos.map((v, i) => {
                const isLast = i === data.videos.length - 1;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '8px',
                        border: `1px solid ${C.g200}`, background: '#F7F7F9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <VideoIcon />
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <a
                          href={v.url} target="_blank" rel="noreferrer"
                          style={{
                            fontFamily: ff, fontSize: '13px', fontWeight: 500, color: C.bk,
                            letterSpacing: '-0.03em', textDecoration: 'none',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {v.title || v.url}
                        </a>
                        <span style={{ fontFamily: ff, fontSize: '12px', color: C.sub, letterSpacing: '-0.03em' }}>
                          {v.platform}
                        </span>
                      </div>
                    </div>
                    {!isLast && <div style={{ height: '1px', background: C.g200 }} />}
                  </div>
                );
              })
            }
          </div>
        </div>
      </div>

      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </>
  );
}
